<template>
    <vue-nav-bar></vue-nav-bar>

    <div class="row">
        <div class="col-md-3 p-3 sidebar-container">
            <!-- Return Home Button -->
            <div class="text-start ms-3 mt-2 mb-4">
                <RouterLink to='/' class="btn btn-outline-primary rounded-pill px-3 py-1 border border-4">
                    <i class="fa-solid fa-house"></i>
                </RouterLink>
            </div>

            <!-- Applicant Tabs -->
            <ul class="list-unstyled ps-3 ms-4">
                <li v-for="item in sidebarItems" :key="item.key" class="sidebar-item d-flex align-items-center mb-3"
                    :class="{ active: activeTab === item.key }" @click="activeTab = item.key">
                    <div class="icon-wrapper">
                        <i :class="[item.icon, 'sidebar-icon']"></i>
                    </div>
                    <span>{{ $Translate(item.title) }}</span>
                </li>
            </ul>
        </div>

        <div class="col-md-9 bg-white p-3">
            <!-- Personal Information Tab -->
            <div v-if="activeTab === 'personalInformationTab'">
                <div class="text-center d-flex justify-content-start">
                    <div class="ms-4">
                        <h4 class="mt-2">{{ $Translate('personalInformationTitle') }}</h4>
                    </div>
                </div>
                <hr class="mt-3 me-3">

                <div class="me-4 d-flex justify-content-end">
                    <button class="btn btn-primary" type="button" @click="openModal()">
                        <i class="fa-solid fa-key me-2"></i> {{ $Translate('resetPassword') }}
                    </button>
                </div>

                <!-- Personal Information Form -->
                <form id="PersonalInformationForm" class="needs-validation p-4" novalidate>
                    <fieldset :disabled="isDisabled">
                        <div class="row g-3">
                            <!-- Name Field -->
                            <div class="col-md-4">
                                <label for="applicantName"><strong>{{ $Translate('name') }}: </strong><span
                                        class="text-danger">*</span></label>
                                <input type="text" id="applicantName" class="form-control"
                                    :placeholder="$Translate('namePlaceholder')"
                                    :class="{ 'is-valid': personalSubmitted && nameIsValid, 'is-invalid': personalSubmitted && !nameIsValid }"
                                    v-model="ApplicantPersonalInformation.name" name="applicantName" required />
                            </div>

                            <!-- Arabic Name Field -->
                            <div class="col-md-4">
                                <label for="arabicName"><strong>{{ $Translate('arabicName') }}: </strong></label>
                                <input type="text" id="arabicName" class="form-control"
                                    :placeholder="$Translate('arabicNamePlaceholder')"
                                    :class="{ 'is-valid': personalSubmitted && arabicNameIsValid, 'is-invalid': personalSubmitted && !arabicNameIsValid }"
                                    v-model="ApplicantPersonalInformation.arabicName" name="arabicName" />
                            </div>

                            <!-- National ID (CPR) Field -->
                            <div class="col-md-4">
                                <label for="nationalId"><strong>{{ $Translate('nationalId') }}: </strong></label>
                                <input type="text" id="nationalId" class="form-control"
                                    :placeholder="$Translate('nationalIdPlaceholder')"
                                    :class="{ 'is-valid': personalSubmitted && nationalIdIsValid, 'is-invalid': personalSubmitted && !nationalIdIsValid }"
                                    v-model="ApplicantPersonalInformation.nationalId" name="nationalId" required />
                            </div>

                            <!-- Country Field -->
                            <div class="col-md-4">
                                <label for="countryRegionId"><strong>{{ $Translate('country') }}: </strong><span
                                        class="text-danger">*</span></label>
                                <select class="form-control"
                                    :class="{ 'is-valid': personalSubmitted && countryRegionIdIsValid, 'is-invalid': personalSubmitted && !countryRegionIdIsValid }"
                                    id="countryRegionId" v-model="ApplicantPersonalInformation.countryRegionId"
                                    required>
                                    <option v-for="country in countryList" :key="country.shortName"
                                        :value="country.countryRegionId">
                                        {{ country.shortName }}
                                    </option>
                                </select>
                            </div>

                            <!-- Gender Field -->
                            <div class="col-md-4">
                                <label for="gender"><strong>{{ $Translate('gender') }}: </strong><span
                                        class="text-danger">*</span></label>
                                <select class="form-control"
                                    :class="{ 'is-valid': personalSubmitted && genderIsValid, 'is-invalid': personalSubmitted && !genderIsValid }"
                                    id="gender" v-model="ApplicantPersonalInformation.genderName" required>
                                    <option v-for="gender in genderList.filter(g => g.name !== '')" :key="gender.name"
                                        :value="gender.name">
                                        {{ gender.name }}
                                    </option>
                                </select>
                            </div>

                            <!-- Date Of Birth Field -->
                            <div class="col-md-4">
                                <label for="dateOfBirth"><strong>{{ $Translate('dateOfBirth') }}: </strong><span
                                        class="text-danger">*</span></label>
                                <input type="date" id="dateOfBirth" class="form-control"
                                    :class="{ 'is-valid': personalSubmitted && dateOfBirthIsValid, 'is-invalid': personalSubmitted && !dateOfBirthIsValid }"
                                    v-model="ApplicantPersonalInformation.birthDate" name="dateOfBirth" required />
                            </div>

                            <!-- Email Field -->
                            <div class="col-md-4">
                                <label for="email"><strong>{{ $Translate('email') }}: </strong><span
                                        class="text-danger">*</span></label>
                                <input type="email" id="email" class="form-control"
                                    :placeholder="$Translate('emailPlaceholder')"
                                    :class="{ 'is-valid': personalSubmitted && emailIsValid, 'is-invalid': personalSubmitted && !emailIsValid }"
                                    v-model="ApplicantPersonalInformation.email" name="email" required />
                            </div>

                            <!-- Confirm Email Field -->
                            <div class="col-md-4">
                                <label for="confirmEmail"><strong>{{ $Translate('confirmEmail') }}: </strong><span
                                        class="text-danger">*</span></label>
                                <input type="email" id="confirmEmail" class="form-control"
                                    :placeholder="$Translate('confirmEmailPlaceholder')"
                                    :class="{ 'is-valid': personalSubmitted && confirmEmailIsValid, 'is-invalid': personalSubmitted && !confirmEmailIsValid }"
                                    v-model="ApplicantPersonalInformation.email" name="confirmEmail" required />
                            </div>

                            <!-- Mobile Number Field -->
                            <div class="col-md-4">
                                <label for="phone"><strong>{{ $Translate('phone') }}: </strong><span
                                        class="text-danger">*</span></label>
                                <input type="text" id="phone" class="form-control"
                                    :placeholder="$Translate('phonePlaceholder')"
                                    :class="{ 'is-valid': personalSubmitted && phoneIsValid, 'is-invalid': personalSubmitted && !phoneIsValid }"
                                    v-model="ApplicantPersonalInformation.phone" name="phone" required />
                            </div>

                            <!-- LinkedIn Field -->
                            <div class="col-md-4">
                                <label for="linkedInLink"><strong>{{ $Translate('linkedInLink') }}:
                                    </strong></label>
                                <input type="text" id="linkedInLink" class="form-control"
                                    :placeholder="$Translate('linkedInLinkPlaceholder')"
                                    :class="{ 'is-valid': personalSubmitted && linkedInLinkIsValid, 'is-invalid': personalSubmitted && !linkedInLinkIsValid }"
                                    v-model="ApplicantPersonalInformation.linkedInLink" name="linkedInLink" required />
                            </div>

                            <!-- Years Of Experience Field -->
                            <div class="col-md-4">
                                <label for="yearOfExperience"><strong>{{ $Translate('yearsOfExperience') }}:
                                    </strong><span class="text-danger">*</span></label>
                                <input type="number" id="yearOfExperience" class="form-control"
                                    :class="{ 'is-valid': personalSubmitted && yearOfExperienceIsValid, 'is-invalid': personalSubmitted && !yearOfExperienceIsValid }"
                                    v-model="ApplicantPersonalInformation.yearsOfExperience" name="yearOfExperience"
                                    required />
                            </div>

                        </div>
                    </fieldset>

                    <div class="row">
                        <div class="buttons-container d-flex justify-content-center mt-5" v-if="isDisabled">
                            <button class="btn btn-primary" style="min-width: 180px;" type="button" @click="edit()">
                                <i class="fa-solid fa-pen-to-square me-2"></i> {{ $Translate('edit') }}
                            </button>
                        </div>

                        <div class="buttons-container d-flex justify-content-center mt-5" v-if="!isDisabled">
                            <button class="btn btn-primary" style="min-width: 180px;" type="button"
                                @click.prevent="update()">
                                <i class="fa-solid fa-floppy-disk me-2"></i> {{ $Translate('update') }}
                            </button>
                        </div>
                    </div>
                </form>
            </div>

            <!-- Education Tab -->
            <div v-if="activeTab === 'educationTab'">
                <div class="text-center d-flex justify-content-start">
                    <div class="ms-4">
                        <h4 class="mt-2">{{ $Translate('educationTitle') }}</h4>
                    </div>
                </div>
                <hr class="mt-3 me-3">

                <!-- Education Form -->
                <form id="EducationForm" class="needs-validation p-4" novalidate>
                    <div class="row g-3">
                        <!-- Major Field -->
                        <div class="col-md-4">
                            <label for="educationDiscipline"><strong>{{ $Translate('educationDiscipline') }}:
                                </strong><span class="text-danger">*</span></label>
                            <input type="text" id="educationDiscipline" class="form-control"
                                :placeholder="$Translate('educationDisciplinePlaceholder')"
                                :class="{ 'is-valid': eduSubmitted && educationDisciplineIsValid, 'is-invalid': eduSubmitted && !educationDisciplineIsValid }"
                                v-model="newEducation.educationDisciplineDescription" name="educationDiscipline"
                                required />
                        </div>

                        <!-- Institute Field -->
                        <div class="col-md-4">
                            <label for="educationInstitute"><strong>{{ $Translate('educationInstitute') }}:
                                </strong><span class="text-danger">*</span></label>
                            <input type="text" id="educationInstitute" class="form-control"
                                :placeholder="$Translate('educationInstitutePlaceholder')"
                                :class="{ 'is-valid': eduSubmitted && educationInstituteIsValid, 'is-invalid': eduSubmitted && !educationInstituteIsValid }"
                                v-model="newEducation.educationInstitutionDescription" name="educationInstitute"
                                required />
                        </div>

                        <!-- Education Level Field -->
                        <div class="col-md-4">
                            <label for="educationLevel"><strong>{{ $Translate('educationLevel') }}: </strong><span
                                    class="text-danger">*</span></label>
                            <select class="form-control"
                                :class="{ 'is-valid': eduSubmitted && educationLevelIsValid, 'is-invalid': eduSubmitted && !educationLevelIsValid }"
                                id="educationLevel" v-model="newEducation.educationLevelDescription" required>
                                <option value="" selected disabled>{{ $Translate('selectEducationLevel') }}</option>
                                <option v-for="edu in educationalLevelsList" :key="edu.recId" :value="edu.recId">
                                    {{ edu.description }}
                                </option>
                            </select>
                        </div>

                        <!-- Grade  Field -->
                        <div class="col-md-4">
                            <label for="gradePoint"><strong>{{ $Translate('gradePoint') }}: </strong></label>
                            <input type="text" id="gradePoint" class="form-control"
                                :placeholder="$Translate('gradePointPlaceholder')"
                                :class="{ 'is-valid': eduSubmitted && gradePointIsValid, 'is-invalid': eduSubmitted && !gradePointIsValid }"
                                v-model="newEducation.gradePointAverage" name="gradePoint" />
                        </div>

                        <!-- From Date Field -->
                        <div class="col-md-4">
                            <label for="startDate"><strong>{{ $Translate('startDate') }}: </strong></label>
                            <input type="date" id="startDate" class="form-control"
                                :class="{ 'is-valid': eduSubmitted && startDateIsValid, 'is-invalid': eduSubmitted && !startDateIsValid }"
                                v-model="newEducation.startDate" name="startDate" />
                        </div>

                        <!-- To Date Field -->
                        <div class="col-md-4">
                            <label for="endDate"><strong>{{ $Translate('endDate') }}: </strong><span
                                    class="text-danger">*</span></label>
                            <input type="date" id="endDate" class="form-control"
                                :class="{ 'is-valid': eduSubmitted && endDateIsValid, 'is-invalid': eduSubmitted && !endDateIsValid }"
                                v-model="newEducation.endDate" name="endDate" required />
                        </div>

                        <!-- Description Field -->
                        <div class="col-md-4">
                            <label for="description"><strong>{{ $Translate('description') }}: </strong></label>
                            <textarea id="description" class="form-control"
                                :class="{ 'is-valid': eduSubmitted && descriptionIsValid, 'is-invalid': eduSubmitted && !descriptionIsValid }"
                                v-model="newEducation.description" name="description" />
                        </div>
                    </div>

                    <!-- Add Education Button -->
                    <div style="display: flex; justify-content: end;">
                        <button type="button" class="add-btn mb-4 mt-3" @click.prevent="addEducation()">{{
                            $Translate('addBtnText')
                            }}<i class="fa-solid fa-plus"></i></button>
                    </div>

                    <!-- Education Table-->
                    <div class="table-responsive" v-if="ApplicantEducation.length > 0">
                        <table class="table table-striped align-middle text-center">
                            <thead class="table-light">
                                <tr>
                                    <th>#</th>
                                    <th>{{ $Translate('educationDiscipline') }}</th>
                                    <th>{{ $Translate('educationInstitute') }}</th>
                                    <th>{{ $Translate('educationLevel') }}</th>
                                    <th>{{ $Translate('gradePoint') }}</th>
                                    <th>{{ $Translate('startDate') }}</th>
                                    <th>{{ $Translate('endDate') }}</th>
                                    <th>{{ $Translate('description') }}</th>
                                    <th>{{ $Translate('action') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, index) in ApplicantEducation" :key="index">
                                    <th scope="row">{{ index + 1 }}</th>
                                    <td>{{ item.educationDisciplineDescription }}</td>
                                    <td>{{ item.educationInstitutionDescription }}</td>
                                    <td>{{ item.educationLevelDescription }}</td>
                                    <td>{{ item.gradePointAverage }}</td>
                                    <td>{{ $ConvertToDateFormat(item.startDate) }}</td>
                                    <td>{{ $ConvertToDateFormat(item.endDate) }}</td>
                                    <td :title="item.description"
                                        style="max-width: 200px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                                        {{ item.description }}</td>
                                    <td>
                                        <button type="button" class="btn btn-sm btn-danger"
                                            @click="showModal('education', item.recId)">
                                            <i class="fa fa-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </form>

            </div>

            <!-- Skills Tab -->
            <div v-if="activeTab === 'skillsTab'">
                <div class="text-center d-flex justify-content-start">
                    <div class="ms-4">
                        <h4 class="mt-2">{{ $Translate('skillsTitle') }}</h4>
                    </div>
                </div>
                <hr class="mt-3 me-3">

                <!-- Skills Form -->
                <form id="SkillForm" class="needs-validation p-4" novalidate>

                    <div class="row g-3">
                        <!-- Skill Field -->
                        <div class="col-md-4">
                            <label for="skillId"><strong>{{ $Translate('skill') }}: </strong><span
                                    class="text-danger">*</span></label>
                            <select class="form-control"
                                :class="{ 'is-valid': skillSubmitted && skillIdIsValid, 'is-invalid': skillSubmitted && !skillIdIsValid }"
                                id="skillId" v-model="newSkill.recId" @change="GetSkillRating(newSkill.recId)" required>
                                <option value="" selected disabled>{{ $Translate('selectSkillId') }}</option>
                                <option v-for="skill in skillsList.filter(s => s.description !== '')" :key="skill.recId"
                                    :value="skill.recId">
                                    {{ skill.description }}
                                </option>
                            </select>
                        </div>

                        <!-- Rating Level Field -->
                        <div class="col-md-4" v-if="(this.newSkill.recId != '')">
                            <label for="ratingLevelId"><strong>{{ $Translate('ratingLevel') }}: </strong><span
                                    class="text-danger">*</span></label>
                            <select class="form-control"
                                :class="{ 'is-valid': skillSubmitted && ratingLevelIdIsValid, 'is-invalid': skillSubmitted && !ratingLevelIdIsValid }"
                                id="ratingLevelId" v-model="newSkill.ratingLevelId" required>
                                <option value="" selected disabled>{{ $Translate('selectRatingLevel') }}</option>
                                <option v-for="rt in SkillRatingLevelList" :key="rt.recId" :value="rt.recId">
                                    {{ rt.description }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <!-- Add Skill Button -->
                    <div style="display: flex; justify-content: end;">
                        <button type="button" class="add-btn mb-4 mt-3" @click.prevent="addSkill()">{{
                            $Translate('addBtnText')
                        }}<i class="fa-solid fa-plus"></i></button>
                    </div>

                    <!-- Skill Table-->
                    <div class="table-responsive" v-if="ApplicantSkill.length > 0">
                        <table class="table table-striped align-middle text-center">
                            <thead class="table-light">
                                <tr>
                                    <th>#</th>
                                    <th>{{ $Translate('skill') }}</th>
                                    <th>{{ $Translate('ratingLevel') }}</th>
                                    <th>{{ $Translate('action') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, index) in ApplicantSkill" :key="index">
                                    <th scope="row">{{ index + 1 }}</th>
                                    <td>{{ item.skillDescription }}</td>
                                    <td>{{ item.levelDescription }}</td>
                                    <td>
                                        <button type="button" class="btn btn-sm btn-danger"
                                            @click="showModal('skill', item.recId)">
                                            <i class="fa fa-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </form>
            </div>

            <!-- 'Professional Experience Tab -->
            <div v-if="activeTab === 'professionalExperienceTab'">
                <div class="text-center d-flex justify-content-start">
                    <div class="ms-4">
                        <h4 class="mt-2">{{ $Translate('professionalExperienceTitle') }}</h4>
                    </div>
                </div>
                <hr class="mt-3 me-3">

                <!-- Professional Experience Form -->
                <form id="ProfessionalExperienceForm" class="needs-validation p-4" novalidate>
                    <div class="row g-3">
                        <!-- Employment Type Field -->
                        <div class="col-md-4">
                            <label for="employmentType"><strong>{{ $Translate('employmentType') }}: </strong><span
                                    class="text-danger">*</span></label>
                            <select class="form-control"
                                :class="{ 'is-valid': profExpSubmitted && employmentTypeIsValid, 'is-invalid': profExpSubmitted && !employmentTypeIsValid }"
                                id="employmentType" v-model="newProfessionalExperience.employerLocation" required>
                                <option value="" selected disabled>{{ $Translate('selectEmploymentType') }}</option>
                                <option value="Employee">{{ $Translate('employee') }}</option>
                                <option value="Unemployed">{{ $Translate('unEmployed') }}</option>
                            </select>
                        </div>

                        <!-- UnEmployment Type Field -->
                        <div class="col-md-4" v-if="newProfessionalExperience.employerLocation === 'Unemployed'">
                            <label for="UnEmployment"><strong>{{ $Translate('UnEmploymentType') }}:
                                </strong><span class="text-danger">*</span></label>
                            <select class="form-control"
                                :class="{ 'is-valid': profExpSubmitted && employerPositionIsValid, 'is-invalid': profExpSubmitted && !employerPositionIsValid }"
                                id="employerPosition" v-model="newProfessionalExperience.employerPosition" required>
                                <option value="" selected disabled>{{ $Translate('selectUnEmploymentType') }}</option>
                                <option value="Job Seeker">{{ $Translate('jobSeeker') }}</option>
                                <option value="Fresh Graduate">{{ $Translate('freshGraduate') }}</option>
                            </select>
                        </div>

                        <!-- Position Field -->
                        <div class="col-md-4" v-if="newProfessionalExperience.employerLocation === 'Employee'">
                            <label for="employerPosition"><strong>{{ $Translate('employerPosition') }}:
                                </strong><span class="text-danger">*</span></label>
                            <input type="text" id="employerPosition" class="form-control"
                                :placeholder="$Translate('employerPositionPlaceholder')"
                                :class="{ 'is-valid': profExpSubmitted && employerPositionIsValid, 'is-invalid': profExpSubmitted && !employerPositionIsValid }"
                                v-model="newProfessionalExperience.employerPosition" name="employerPosition" required />
                        </div>

                        <!-- Employer Name Field -->
                        <div class="col-md-4" v-if="newProfessionalExperience.employerLocation === 'Employee'">
                            <label for="nameOfEmployer"><strong>{{ $Translate('nameOfEmployer') }}: </strong><span
                                    class="text-danger">*</span></label>
                            <input type="text" id="nameOfEmployer" class="form-control"
                                :placeholder="$Translate('nameOfEmployerPlaceholder')"
                                :class="{ 'is-valid': profExpSubmitted && nameOfEmployerIsValid, 'is-invalid': profExpSubmitted && !nameOfEmployerIsValid }"
                                v-model="newProfessionalExperience.employerName" name="nameOfEmployer" required />
                        </div>

                        <!-- From Date Field -->
                        <div class="col-md-4">
                            <label for="startDateProfessionalExperience"><strong>{{ $Translate('startDate') }}:
                                </strong><span class="text-danger">*</span></label>
                            <input type="date" id="startDateProfessionalExperience" class="form-control"
                                :class="{ 'is-valid': profExpSubmitted && startDateProfessionalExperienceIsValid, 'is-invalid': profExpSubmitted && !startDateProfessionalExperienceIsValid }"
                                v-model="newProfessionalExperience.startDate" name="startDateProfessionalExperience"
                                required />
                        </div>

                        <!-- To Date Field -->
                        <div class="col-md-4">
                            <label for="endDateProfessionalExperience"><strong>{{ $Translate('endDate') }}:
                                </strong><span class="text-danger">*</span></label>
                            <input type="date" id="endDateProfessionalExperience" class="form-control"
                                :disabled="isChecked" :class="{
                                    'is-valid': profExpSubmitted && endDateProfessionalExperienceIsValid,
                                    'is-invalid': profExpSubmitted && !endDateProfessionalExperienceIsValid
                                }" v-model="newProfessionalExperience.endDate" />
                            <div class="form-check">
                                <input class="form-check-input" type="checkbox" id="present" v-model="isChecked"
                                    @change="toggleCheck" />
                                <label class="form-check-label" for="present">
                                    {{ $Translate('present') }}
                                </label>
                            </div>

                        </div>

                        <!-- Description Field -->
                        <div class="col-md-4">
                            <label for="descriptionProfessionalExperience"><strong>{{ $Translate('description') }}:
                                </strong></label>
                            <textarea id="descriptionProfessionalExperience" class="form-control"
                                :class="{ 'is-valid': profExpSubmitted && descriptionProfessionalExperienceIsValid, 'is-invalid': profExpSubmitted && !descriptionProfessionalExperienceIsValid }"
                                v-model="newProfessionalExperience.note" name="descriptionProfessionalExperience" />
                        </div>

                        <!-- Add Professional Experience Button -->
                        <div style="display: flex; justify-content: end;">
                            <button type="button" class="add-btn mb-4 mt-3"
                                @click.prevent="addProfessionalExperience()">{{
                                    $Translate('addBtnText')
                                }}<i class="fa-solid fa-plus"></i></button>
                        </div>

                        <!-- Professional Experience Table-->
                        <div class="table-responsive" v-if="ApplicantProfessionalExperience.length > 0">
                            <table class="table table-striped align-middle text-center">
                                <thead class="table-light">
                                    <tr>
                                        <th>#</th>
                                        <th>{{ $Translate('employmentType') }}</th>
                                        <th>{{ $Translate('employerPosition') }}</th>
                                        <th>{{ $Translate('nameOfEmployer') }}</th>
                                        <th>{{ $Translate('startDate') }}</th>
                                        <th>{{ $Translate('endDate') }}</th>
                                        <th>{{ $Translate('description') }}</th>
                                        <th>{{ $Translate('action') }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(item, index) in ApplicantProfessionalExperience" :key="index">
                                        <th scope="row">{{ index + 1 }}</th>
                                        <td>{{ item.employerLocation }}</td>
                                        <td>{{ item.employerPosition }}</td>
                                        <td>{{ item.employerName }}</td>
                                        <td>{{ $ConvertToDateFormat(item.startDate) }}</td>
                                        <td>{{ (item.endDate) === "1900-01-01T12:00:00" ? $Translate('present') :
                                            $ConvertToDateFormat(item.endDate) }}</td>
                                        <td :title="item.note"
                                            style="max-width: 200px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                                            {{ item.note }}</td>
                                        <td>
                                            <button type="button" class="btn btn-sm btn-danger"
                                                @click="showModal('professionalExperience', item.recId)">
                                                <i class="fa fa-trash"></i>
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </form>
            </div>

            <!-- Certificates Tab -->
            <div v-if="activeTab === 'certificatesTab'">
                <div class="text-center d-flex justify-content-start">
                    <div class="ms-4">
                        <h4 class="mt-2">{{ $Translate('certificatesTitle') }}</h4>
                    </div>
                </div>
                <hr class="mt-3 me-3">

                <!-- Certificates Form -->
                <form id="CertificateForm" class="needs-validation p-4" novalidate>
                    <div class="row g-3">
                        <!-- Certificate Field -->
                        <div class="col-md-4">
                            <label for="certificateTypeId"><strong>{{ $Translate('certificate') }}: </strong><span
                                    class="text-danger">*</span></label>
                            <select class="form-control"
                                :class="{ 'is-valid': certSubmitted && certificateTypeIdIsValid, 'is-invalid': certSubmitted && !certificateTypeIdIsValid }"
                                id="certificateTypeId" v-model="newCertificate.certificateTypeId" required>
                                <option value="" selected disabled>{{ $Translate('selectCertificate') }}</option>
                                <option v-for="cert in certificatesList.filter(c => c.description !== '')"
                                    :key="cert.recId" :value="cert.recId">
                                    {{ cert.description }}
                                </option>
                            </select>
                        </div>

                        <!-- Unlisted Certificate Field -->
                        <div class="col-md-4">
                            <label for="unlistedCertificate"><strong>{{ $Translate('unlistedCertificate') }}:
                                </strong></label>
                            <input type="text" id="unlistedCertificate" class="form-control"
                                :placeholder="$Translate('unlistedCertificatePlaceholder')"
                                :class="{ 'is-valid': certSubmitted && unlistedCertificateIsValid, 'is-invalid': certSubmitted && !unlistedCertificateIsValid }"
                                v-model="newCertificate.unlistedCertificate" name="unlistedCertificate" />
                        </div>

                        <!-- From Date Field -->
                        <div class="col-md-4">
                            <label for="startDateCertificate"><strong>{{ $Translate('startDate') }}: </strong><span
                                    class="text-danger">*</span></label>
                            <input type="date" id="startDateCertificate" class="form-control"
                                :class="{ 'is-valid': certSubmitted && startDateCertificateIsValid, 'is-invalid': certSubmitted && !startDateCertificateIsValid }"
                                v-model="newCertificate.startDateCertificate" name="startDateCertificate" />
                        </div>

                        <!-- To Date Field -->
                        <div class="col-md-4">
                            <label for="endDateCertificate"><strong>{{ $Translate('startDate') }}: </strong><span
                                    class="text-danger">*</span></label>
                            <input type="date" id="endDateCertificate" class="form-control"
                                :class="{ 'is-valid': certSubmitted && endDateCertificateIsValid, 'is-invalid': certSubmitted && !endDateCertificateIsValid }"
                                v-model="newCertificate.endDateCertificate" name="endDateCertificate" />
                        </div>

                        <!-- Description Field -->
                        <div class="col-md-4">
                            <label for="descriptionCertificate"><strong>{{ $Translate('description') }}:
                                </strong></label>
                            <textarea id="descriptionCertificate" class="form-control"
                                :class="{ 'is-valid': certSubmitted && descriptionCertificateIsValid, 'is-invalid': certSubmitted && !descriptionCertificateIsValid }"
                                v-model="newCertificate.descriptionCertificate" name="descriptionCertificate" />
                        </div>
                    </div>

                    <!-- Radio (Upload Type) -->
                    <div class="d-flex align-items-center gap-4 mb-2 mt-3">
                        <div class="custom-control custom-radio custom-control-inline">
                            <input type="radio" id="certificateFile" value="file"
                                v-model="newCertificate.certificateType" class="custom-control-input"
                                name="certificateType" />
                            <label class="custom-control-label ms-2" for="certificateFile">{{ $Translate('uploadFile')
                            }}</label>
                        </div>

                        <div class="custom-control custom-radio custom-control-inline">
                            <input type="radio" id="certificateURL" value="url" v-model="newCertificate.certificateType"
                                class="custom-control-input" name="certificateType" />
                            <label class="custom-control-label ms-2" for="certificateURL">{{ $Translate('uploadUrl')
                            }}</label>
                        </div>
                    </div>

                    <!-- Label -->
                    <div class="d-flex justify-content-start mb-2">
                        <label><strong>{{ $Translate('certificate') }}:</strong></label>
                    </div>

                    <!-- File/URL Input -->
                    <div class="mb-3">
                        <div class="fileArea" v-if="newCertificate.certificateType === 'file'"
                            style="background-color: white;">
                            <input type="file" accept=".pdf" class="form-control" :class="{
                                'is-valid': certSubmitted && certificateUploadTypeIsValid,
                                'is-invalid': certSubmitted && !certificateUploadTypeIsValid
                            }" @change="handleCertificateFileUpload" />
                        </div>
                        <div class="urlArea" v-if="newCertificate.certificateType === 'url'"
                            style="background-color: white;">
                            <input type="url" style="border: 0px; width: 100%;"
                                :placeholder="$Translate('certificateLink')" v-model="newCertificate.certificateLink"
                                class="form-control" :class="{
                                    'is-valid': certSubmitted && certificateUploadTypeIsValid,
                                    'is-invalid': certSubmitted && !certificateUploadTypeIsValid
                                }" />
                        </div>
                    </div>

                    <!-- Add Certificate Button -->
                    <div style="display: flex; justify-content: end;">
                        <button type="button" class="add-btn mb-4 mt-3" @click.prevent="addCertificate()">{{
                            $Translate('addBtnText')
                        }}<i class="fa-solid fa-plus"></i></button>
                    </div>

                    <!-- Certificate Table-->
                    <div class="table-responsive" v-if="ApplicantCertificate.length > 0">
                        <table class="table table-striped align-middle text-center">
                            <thead class="table-light">
                                <tr>
                                    <th>{{ $Translate('certificate') }}</th>
                                    <th>{{ $Translate('startDate') }}</th>
                                    <th>{{ $Translate('endDate') }}</th>
                                    <th>{{ $Translate('description') }}</th>
                                    <th>{{ $Translate('action') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, index) in ApplicantCertificate" :key="index">
                                    <td>{{ item.description }}</td>
                                    <td>{{ $ConvertToDateFormat(item.issueDate) }}</td>
                                    <td>{{ $ConvertToDateFormat(item.expirationDate) }}</td>
                                    <td :title="item.note"
                                        style="max-width: 200px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                                        {{ item.note }}</td>
                                    <td>
                                        <button type="button" class="btn btn-sm btn-danger"
                                            @click="showModal('certificate', item.recId)">
                                            <i class="fa fa-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </form>
            </div>

            <!-- Courses Tab -->
            <div v-if="activeTab === 'coursesTab'">
                <div class="text-center d-flex justify-content-start">
                    <div class="ms-4">
                        <h4 class="mt-2">{{ $Translate('coursesTitle') }}</h4>
                    </div>
                </div>
                <hr class="mt-3 me-3">

                <!-- Courses Form -->
                <form id="CourseForm" class="needs-validation p-4" novalidate>
                    <div class="row g-3">
                        <!-- Course Name Field -->
                        <div class="col-md-4">
                            <label for="courseName"><strong>{{ $Translate('courseName') }}: </strong><span
                                    class="text-danger">*</span></label>
                            <input type="text" id="courseName" class="form-control"
                                :placeholder="$Translate('courseNamePlaceholder')"
                                :class="{ 'is-valid': courseSubmitted && courseNameIsValid, 'is-invalid': courseSubmitted && !courseNameIsValid }"
                                v-model="newCourse.courseName" name="courseName" required />
                        </div>

                        <!-- From Date Field -->
                        <div class="col-md-4">
                            <label for="startDateCourse"><strong>{{ $Translate('startDate') }}:
                                </strong><span class="text-danger">*</span></label>
                            <input type="date" id="startDateCourse" class="form-control"
                                :class="{ 'is-valid': courseSubmitted && startDateCourseIsValid, 'is-invalid': courseSubmitted && !startDateCourseIsValid }"
                                v-model="newCourse.startDateCourse" name="startDateCourse" required />
                        </div>

                        <!-- To Date Field -->
                        <div class="col-md-4">
                            <label for="endDateCourse"><strong>{{ $Translate('endDate') }}:
                                </strong><span class="text-danger">*</span></label>
                            <input type="date" id="endDateCourse" class="form-control"
                                :class="{ 'is-valid': courseSubmitted && endDateCourseIsValid, 'is-invalid': courseSubmitted && !endDateCourseIsValid }"
                                v-model="newCourse.endDateCourse" name="endDateCourse" required />
                        </div>

                        <!-- Description Field -->
                        <div class="col-md-4">
                            <label for="descriptionCourse"><strong>{{ $Translate('description') }}:
                                </strong></label>
                            <textarea id="descriptionCourse" class="form-control"
                                :class="{ 'is-valid': courseSubmitted && descriptionCourseIsValid, 'is-invalid': courseSubmitted && !descriptionCourseIsValid }"
                                v-model="newCourse.descriptionCourse" name="descriptionCourse" />
                        </div>
                    </div>

                    <!-- Add Course Button -->
                    <div style="display: flex; justify-content: end;">
                        <button type="button" class="add-btn mb-4 mt-3" @click.prevent="addCourse()">{{
                            $Translate('addBtnText')
                            }}<i class="fa-solid fa-plus"></i></button>
                    </div>

                    <!-- Course Table-->
                    <div class="table-responsive" v-if="ApplicantCourse.length > 0">
                        <table class="table table-striped align-middle text-center">
                            <thead class="table-light">
                                <tr>
                                    <th>{{ $Translate('courseName') }}</th>
                                    <th>{{ $Translate('startDate') }}</th>
                                    <th>{{ $Translate('endDate') }}</th>
                                    <th>{{ $Translate('description') }}</th>
                                    <th>{{ $Translate('action') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, index) in ApplicantCourse" :key="index">
                                    <td>{{ item.description }}</td>
                                    <td>{{ $ConvertToDateFormat(item.startDate) }}</td>
                                    <td>{{ $ConvertToDateFormat(item.endDate) }}</td>
                                    <td :title="item.courseDescription"
                                        style="max-width: 200px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                                        {{ item.courseDescription }}</td>
                                    <td>
                                        <button type="button" class="btn btn-sm btn-danger"
                                            @click="showModal('course', item.recId)">
                                            <i class="fa fa-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </form>
            </div>

            <!-- Projects Experience Tab -->
            <div v-if="activeTab === 'projectExperienceTab'">
                <div class="text-center d-flex justify-content-start">
                    <div class="ms-4">
                        <h4 class="mt-2">{{ $Translate('projectExperienceTitle') }}</h4>
                    </div>
                </div>
                <hr class="mt-3 me-3">

                <!-- Project Experience Form -->
                <form id="ProjectExperienceForm" class="needs-validation p-4" novalidate>
                    <div class="row g-3">
                        <!-- Role Field -->
                        <div class="col-md-4">
                            <label for="role"><strong>{{ $Translate('role') }}: </strong><span
                                    class="text-danger">*</span></label>
                            <input type="text" id="role" class="form-control"
                                :placeholder="$Translate('rolePlaceholder')"
                                :class="{ 'is-valid': projExpSubmitted && roleIsValid, 'is-invalid': projExpSubmitted && !roleIsValid }"
                                v-model="newProjectExperience.role" name="role" required />
                        </div>

                        <!-- From Date Field -->
                        <div class="col-md-4">
                            <label for="startDateProjectExperience"><strong>{{ $Translate('startDate') }}:
                                </strong><span class="text-danger">*</span></label>
                            <input type="date" id="startDateProjectExperience" class="form-control"
                                :class="{ 'is-valid': projExpSubmitted && startDateProjectExperienceIsValid, 'is-invalid': projExpSubmitted && !startDateProjectExperienceIsValid }"
                                v-model="newProjectExperience.startDateProjectExperience"
                                name="startDateProjectExperience" required />
                        </div>

                        <!-- To Date Field -->
                        <div class="col-md-4">
                            <label for="endDateProjectExperience"><strong>{{ $Translate('endDate') }}:
                                </strong><span class="text-danger">*</span></label>
                            <input type="date" id="endDateProjectExperience" class="form-control"
                                :class="{ 'is-valid': projExpSubmitted && endDateProjectExperienceIsValid, 'is-invalid': projExpSubmitted && !endDateProjectExperienceIsValid }"
                                v-model="newProjectExperience.endDateProjectExperience" name="endDateProjectExperience"
                                required />
                        </div>

                        <!-- Description Field -->
                        <div class="col-md-4">
                            <label for="descriptionProjectExperience"><strong>{{ $Translate('description') }}:
                                </strong></label>
                            <textarea id="descriptionProjectExperience" class="form-control"
                                :class="{ 'is-valid': projExpSubmitted && descriptionProjectExperienceIsValid, 'is-invalid': projExpSubmitted && !descriptionProjectExperienceIsValid }"
                                v-model="newProjectExperience.descriptionProjectExperience"
                                name="descriptionProjectExperience" />
                        </div>
                    </div>

                    <!-- Add Project Experience Button -->
                    <div style="display: flex; justify-content: end;">
                        <button type="button" class="add-btn mb-4 mt-3" @click.prevent="addProjectExperience()">{{
                            $Translate('addBtnText')
                        }}<i class="fa-solid fa-plus"></i></button>
                    </div>

                    <!-- Project Experience Table-->
                    <div class="table-responsive" v-if="ApplicantProjectExperience.length > 0">
                        <table class="table table-striped align-middle text-center">
                            <thead class="table-light">
                                <tr>
                                    <th>{{ $Translate('role') }}</th>
                                    <th>{{ $Translate('startDate') }}</th>
                                    <th>{{ $Translate('endDate') }}</th>
                                    <th>{{ $Translate('description') }}</th>
                                    <th>{{ $Translate('action') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, index) in ApplicantProjectExperience" :key="index">
                                    <td>{{ item.role }}</td>
                                    <td>{{ $ConvertToDateFormat(item.roleStartDate) }}</td>
                                    <td>{{ $ConvertToDateFormat(item.roleEndDate) }}</td>
                                    <td :title="item.projectName"
                                        style="max-width: 200px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                                        {{ item.projectName }}</td>
                                    <td>
                                        <button type="button" class="btn btn-sm btn-danger"
                                            @click="showModal('projectExperience', item.recId)">
                                            <i class="fa fa-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </form>
            </div>

            <!-- Resume Tab -->
            <div v-if="activeTab === 'resumeTab'" id="resume">
                <div class="text-center d-flex justify-content-start">
                    <div class="ms-4">
                        <h4 class="mt-2">{{ $Translate('resumeTitle') }}</h4>
                    </div>
                </div>
                <hr class="mt-3 me-3">

                <!-- Resume Form -->
                <form id="ResumeForm" class="needs-validation p-4" novalidate>
                    <!-- Attachment Name Field 
                    <div class="col-md-8 mb-3">
                        <label for="attachmentName"><strong>{{ $Translate('attachmentName') }}: </strong><span
                                class="text-danger">*</span></label>
                        <input type="text" id="attachmentName" class="form-control mt-2"
                            :placeholder="$Translate('attachmentNamePlaceholder')"
                            :class="{ 'is-valid': submitted && attachmentNameIsValid, 'is-invalid': submitted && !attachmentNameIsValid }"
                            v-model="newResume.fileName" name="attachmentName" required />
                    </div>
                    -->

                    <!-- Radio (Upload Type) -->
                    <div class="d-flex align-items-center gap-4 mb-2">
                        <div class="custom-control custom-radio custom-control-inline">
                            <input type="radio" id="resumeFile" value="file" v-model="newResume.resumeType"
                                class="custom-control-input" name="resumeType" />
                            <label class="custom-control-label ms-2" for="resumeFile">{{ $Translate('uploadFile')
                                }}</label>
                        </div>

                        <div class="custom-control custom-radio custom-control-inline">
                            <input type="radio" id="resumeURL" value="url" v-model="newResume.resumeType"
                                class="custom-control-input" name="resumeType" />
                            <label class="custom-control-label ms-2" for="resumeURL">{{ $Translate('uploadUrl')
                                }}</label>
                        </div>
                    </div>

                    <!-- Label -->
                    <div class="d-flex justify-content-start mb-2">
                        <label><strong>{{ $Translate('resume') }}:</strong></label>
                    </div>

                    <!-- File/URL Input -->
                    <div class="mb-3">
                        <div class="fileArea" v-if="newResume.resumeType === 'file'" style="background-color: white;">
                            <input type="file" accept=".pdf" class="form-control" :class="{
                                'is-valid': resumeSubmitted && resumeUploadTypeIsValid,
                                'is-invalid': resumeSubmitted && !resumeUploadTypeIsValid
                            }" @change="handleResumeFileUpload" />
                        </div>
                        <div class="urlArea" v-if="newResume.resumeType === 'url'" style="background-color: white;">
                            <input type="url" style="border: 0px; width: 100%;" :placeholder="$Translate('resumeLink')"
                                v-model="newResume.resumeLink" class="form-control" :class="{
                                    'is-valid': resumeSubmitted && resumeUploadTypeIsValid,
                                    'is-invalid': resumeSubmitted && !resumeUploadTypeIsValid
                                }" />
                        </div>
                    </div>

                    <!-- Add Resume Button -->
                    <div style="display: flex; justify-content: end;">
                        <button type="button" class="add-btn mb-4 mt-3" @click.prevent="addResume()">{{
                            $Translate('addBtnText')
                        }}<i class="fa-solid fa-plus"></i></button>
                    </div>

                    <!-- Resume Table-->
                    <div class="table-responsive" v-if="ApplicantAttachment.length > 0">
                        <table class="table table-striped align-middle text-center">
                            <thead class="table-light">
                                <tr>
                                    <th>{{ $Translate('name') }}</th>
                                    <th>{{ $Translate('action') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, index) in ApplicantAttachment" :key="index">
                                    <td>{{ item.name }}</td>
                                    <td>
                                        <div class="inline-block">
                                            <button type="button" class="btn btn-sm btn-info me-2"
                                                @click="previewAttachment(item.recId)">
                                                <i class="fa fa-eye"></i>
                                            </button>
                                            <button type="button" class="btn btn-sm btn-danger"
                                                @click="showModal('resume', item.recId)">
                                                <i class="fa fa-trash"></i>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                </form>
            </div>

            <!-- My Applications Tab -->
            <div v-if="activeTab === 'applicationTab'">
                <div class="text-center d-flex justify-content-start">
                    <div class="ms-4">
                        <h4 class="mt-2">{{ $Translate('applicationTitle') }}</h4>
                    </div>
                </div>
                <hr class="mt-3 me-3">

                <!-- My Applications -->
                <div class="p-4">
                    <div v-if="ApplicantApplication.length === 0" class="text-center p-5">
                        <img src="../../../../dist/assets/animations/no-data.svg" alt="No Data"
                            style="max-width: 250px; opacity: 0.6;" />
                        <h5 class="mt-4 text-muted">{{ $Translate('noApplicationsFound') }}</h5>
                    </div>


                    <div v-for="app in ApplicantApplication" class="card mb-3 shadow-sm hover-shadow transition" :style="{
                        [isRtl ? 'borderRight' : 'borderLeft']: '4px solid #FC7F3F',
                        borderRadius: '12px'
                    }">
                        <div class="card-body p-3">
                            <div class="d-flex justify-content-between align-items-start flex-wrap mb-3">
                                <!-- Company Logo + Job Info -->
                                <div class="d-flex align-items-center">
                                    <div class="me-3">
                                        <img src="../../../../dist/assets/GITS.png" alt="Company Logo"
                                            style="height: 52px; width: auto; object-fit: contain;">
                                    </div>
                                    <div>
                                        <h6 class="mb-1 fw-bold text-dark">{{ app.jobId }}</h6>
                                        <small class="text-muted">{{ app.department }}</small>
                                    </div>
                                </div>

                                <!-- Status Badge -->
                                <span
                                    class="badge text-white px-4 py-2 mt-2 mt-md-0 d-flex align-items-center justify-content-center box-shadow"
                                    :style="{
                                        'background-color': statusColors[app.status],
                                        'border-radius': '50px',
                                        'font-size': '0.85rem',
                                        'min-width': '110px',
                                        'box-shadow': '0 2px 8px rgba(0, 0, 0, 0.1)'
                                    }">
                                    {{ app.status }}
                                </span>
                            </div>

                            <!-- Submission Date -->
                            <div class="border-top pt-2 mt-2 text-muted" style="font-size: 0.75rem;">
                                <strong>{{ $Translate('submissionDateLabel') }}:</strong> {{
                                    $ConvertToDateFormat(app.dateOfReception) }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Attachment Preview (Pop-Up) -->
    <div class="modal fade" id="AttachmentModal" tabindex="-1" aria-labelledby="AttachmentModal" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-lg">
            <div class="modal-content border border-primary shadow-lg">
                <div class="modal-header bg-primary text-white">
                    <h5 class="modal-title">
                        <!--<i class="fas fa-paperclip me-2"></i>{{ $Translate('attachment') }}-->
                        <i class="fas fa-paperclip me-2"></i>{{ attachmentFileName }}
                    </h5>
                    <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"
                        aria-label="Close"></button>
                </div>
                <div class="modal-body" style="min-height: 500px;">
                    <object v-if="attachmentContent"
                        :data="'data:' + attachmentMimeType + ';base64,' + attachmentContent" :type="attachmentMimeType"
                        class="w-100 h-100" style="min-height: 500px;">
                    </object>
                </div>
                <div class="modal-footer">
                    <div class="d-flex justify-content-between align-items-center w-100">
                        <button type="button" class="btn btn-outline-primary" data-bs-dismiss="modal">
                            {{ $Translate('close') }}
                        </button>
                        <button type="button" class="btn btn-primary"
                            @click="$DownloadBase64File(attachmentMimeType, attachmentContent, attachmentFileName)">
                            <i class="fas fa-file-download me-1"></i> {{ $Translate('download') }}
                        </button>
                    </div>
                </div>

            </div>
        </div>
    </div>



    <!-- Reset Password Modal -->
    <div class="modal fade" id="resetModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content p-3">

                <button type="button" class="btn-close ms-auto" data-bs-dismiss="modal" aria-label="Close"></button>


                <!-- Header -->
                <div class="text-center">
                    <h2 class="mt-3 mb-3"><strong>{{ $Translate('resetPassword') }}</strong></h2>
                    <p>{{ $Translate('resetSubTitle') }}</p>
                </div>

                <!-- Form -->
                <form id="ResetPasswordForm" @submit.prevent="reset">
                    <!-- Current Password -->
                    <div class="form-group position-relative mb-3">
                        <label class="mb-2 me-1"><strong>{{ $Translate('currentPassword') }}:</strong> <span
                                class="text-danger">*</span></label>
                        <input type="password" class="form-control custom-radius"
                            :class="{ 'is-valid': resetSubmitted && resetCurrentPasswordIsValid, 'is-invalid': resetSubmitted && !resetCurrentPasswordIsValid }"
                            name="currentPassword" v-model="currentPassword"
                            :placeholder="$Translate('currentPassword')" required>
                    </div>

                    <!-- New Password -->
                    <div class="form-group position-relative mb-3">
                        <label class="mb-2 me-1"><strong>{{ $Translate('newPassword') }}:</strong> <span
                                class="text-danger">*</span></label>
                        <input type="password" class="form-control custom-radius"
                            :class="{ 'is-valid': resetSubmitted && resetNewPasswordIsValid, 'is-invalid': resetSubmitted && !resetNewPasswordIsValid }"
                            name="newPassword" v-model="newPassword" :placeholder="$Translate('newPassword')" required>
                    </div>

                    <!-- Confirm Password -->
                    <div class="form-group position-relative mb-3">
                        <label class="mb-2 me-1"><strong>{{ $Translate('confirmNewPassword') }}:</strong> <span
                                class="text-danger">*</span></label>
                        <input type="password" class="form-control custom-radius"
                            :class="{ 'is-valid': resetSubmitted && resetConfirmNewPasswordIsValid, 'is-invalid': resetSubmitted && !resetConfirmNewPasswordIsValid }"
                            name="confirmNewPassword" v-model="confirmNewPassword"
                            :placeholder="$Translate('confirmPassword')" required>
                    </div>

                    <div class="mt-4 d-flex justify-content-center">
                        <button type="button" class="btn w-100 custom-radius text-white" style="background: #00013A;"
                            @click.prevent="reset()">
                            {{ $Translate('resetPassword') }}
                        </button>
                    </div>
                </form>

            </div>
        </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div class="modal fade" id="deleteWarningModal" tabindex="-1" aria-labelledby="deleteWarningModalLabel"
        aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content custom-delete-modal text-center px-4 py-3 rounded-4">

                <div class="modal-header border-0 p-0">
                    <button type="button" class="btn-close ms-auto" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>

                <div class="modal-body">
                    <div class="warning-icon mb-4">
                        <img :src="gif2Source" alt="Animated GIF" class="img-fluid" />
                    </div>
                    <h4 class="fw-bold mb-2">{{ $Translate('deleteAlert') }}</h4>
                    <p class="text-muted mb-4">{{ $Translate('deleteWarningMessageTitle') }}</p>

                    <div class="d-flex justify-content-center gap-3">
                        <button type="button" class="btn btn-light px-4 py-2 rounded-3" data-bs-dismiss="modal">
                            {{ $Translate('cancel') }}
                        </button>
                        <button type="button" class="btn btn-danger px-4 py-2 rounded-3" @click="confirmDelete()">
                            {{ $Translate('delete') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Password Changes Successfully Modal -->
    <div class="modal fade" id="changeSuccessModal" tabindex="-1" aria-labelledby="regSuccessModalLabel"
        aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content custom-success-modal text-center px-4 py-3 rounded-4">

                <div class="modal-header border-0 p-0">
                    <button type="button" class="btn-close ms-auto" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>

                <div class="modal-body">
                    <div class="success-icon mb-4">
                        <img :src="gifSource" alt="Animated GIF" class="img-fluid" />
                    </div>
                    <h4 class="fw-bold mb-2">{{ $Translate('changeSuccessMessageTitle') }}</h4>
                    <p class="text-muted mb-4">{{ $Translate('changeSuccessMessageBody') }}</p>

                    <div class="d-flex justify-content-center">
                        <button type="button" class="btn btn-primary px-4 py-2 rounded-3" data-bs-dismiss="modal"
                            @click="Restart">
                            {{ $Translate('close') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Failed Password Change Modal -->
    <div class="modal fade" id="changeFailedModal" tabindex="-1" aria-labelledby="regFailedModalLabel"
        aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content custom-success-modal text-center px-4 py-3 rounded-4">

                <div class="modal-header border-0 p-0">
                    <button type="button" class="btn-close ms-auto" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>

                <div class="modal-body">
                    <div class="failed-icon mb-4">
                        <img :src="gif2Source" alt="Animated GIF" class="img-fluid" />
                    </div>
                    <h4 class="fw-bold mb-2">{{ $Translate('changeFailedMessageTitle') }}</h4>
                    <p class="text-muted mb-4">{{ $Translate('changeFailedMessageBody') }}</p>

                    <div class="d-flex justify-content-center">
                        <button type="button" class="btn btn-primary px-4 py-2 rounded-3" data-bs-dismiss="modal"
                            aria-label="Close">
                            {{ $Translate('close') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <vue-footer :project_name="'Applicant Portal.'"></vue-footer>
</template>

<script>
let deleteWarningModal = null;
let attachmentModal = null;
let resetModal = null;
let changeSuccessModal = null;
let changeFailedModal = null;

import { Modal } from 'bootstrap';
import { RouterLink } from 'vue-router';
import { ref } from 'vue';
import { runWithoutBlocking } from '../../VueGlobal';

export default {
    name: 'ApplicantProfilePage',
    data() {
        return {
            companyId: 'usmf',
            token: GetToken(),
            applicantId: GetApplicantId(),
            activeTab: ref('personalInformationTab'),
            gifSource: require('../../../assets/animations/success.gif'),
            gif2Source: require('../../../assets/animations/warning.gif'),

            sidebarItems: [
                { key: 'personalInformationTab', title: 'personalInformationTitle', icon: 'fas fa-user' },
                { key: 'applicationTab', title: 'applicationTitle', icon: 'fa-solid fa-user-tie' },
                { key: 'educationTab', title: 'educationTitle', icon: 'fas fa-graduation-cap' },
                { key: 'skillsTab', title: 'skillsTitle', icon: 'fas fa-lightbulb' },
                { key: 'professionalExperienceTab', title: 'professionalExperienceTitle', icon: 'fas fa-briefcase' },
                { key: 'certificatesTab', title: 'certificatesTitle', icon: 'fas fa-certificate' },
                { key: 'coursesTab', title: 'coursesTitle', icon: 'fas fa-chalkboard-teacher' },
                { key: 'projectExperienceTab', title: 'projectExperienceTitle', icon: 'fas fa-project-diagram' },
                { key: 'resumeTab', title: 'resumeTitle', icon: 'fas fa-file-alt' }
            ],

            submitted: false,
            personalSubmitted: false,
            eduSubmitted: false,
            skillSubmitted: false,
            profExpSubmitted: false,
            certSubmitted: false,
            courseSubmitted: false,
            projExpSubmitted: false,
            resumeSubmitted: false,
            applicationSubmitted: false,

            isRtl: document.documentElement.dir === 'rtl',

            // Personal Validation
            nameIsValid: false,
            arabicNameIsValid: false,
            nationalIdIsValid: false,
            countryRegionIdIsValid: false,
            genderIsValid: false,
            dateOfBirthIsValid: false,
            emailIsValid: false,
            confirmEmailIsValid: false,
            phoneIsValid: false,
            passwordIsValid: false,
            confirmPasswordIsValid: false,
            linkedInLinkIsValid: false,
            yearOfExperienceIsValid: false,

            ApplicantPersonalInformation: [],
            isDisabled: true,

            // Education Validation
            educationDisciplineIsValid: false,
            educationInstituteIsValid: false,
            educationLevelIsValid: false,
            gradePointIsValid: false,
            startDateIsValid: false,
            endDateIsValid: false,
            descriptionIsValid: false,

            newEducation: {
                educationDisciplineDescription: '',
                educationInstitutionDescription: '',
                educationLevelDescription: '',
                gradePointAverage: '',
                startDate: '',
                endDate: '',
                description: '',
            },
            educationToDeleteRecId: null,
            ApplicantEducation: [],

            // Skills Validation
            skillIdIsValid: false,
            ratingLevelIdIsValid: false,

            newSkill: {
                recId: '', // Will be passed as skillId while creating Applicant Skill
                ratingLevelId: '', // Get the recId from the rating selection to be passed on CreateApplicantSkill
            },
            skillToDeleteRecId: null,

            SkillRatingLevelList: [], // Retrieve the Rating Scale for each skill (from API) 
            ApplicantSkill: [],

            // Professional Experience Validation
            employmentTypeIsValid: false,
            employerPositionIsValid: false,
            nameOfEmployerIsValid: false,
            startDateProfessionalExperienceIsValid: false,
            endDateProfessionalExperienceIsValid: false,
            descriptionProfessionalExperienceIsValid: false,

            newProfessionalExperience: {
                employerLocation: '',
                employerPosition: '',
                employerName: '',
                startDate: '',
                endDate: '',
                note: ''
            },
            professionalExperienceToDeleteRecId: null,
            isChecked: false,

            ApplicantProfessionalExperience: [],

            // Certificates Validation
            certificateTypeIdIsValid: false,
            unlistedCertificateIsValid: false,
            startDateCertificateIsValid: false,
            endDateCertificateIsValid: false,
            descriptionCertificateIsValid: false,
            certificateFileIsValid: false,
            certificateURLIsValid: false,
            certificateLinkIsValid: false,
            certificateUploadTypeIsValid: false,

            newCertificate: {
                certificateTypeId: '',
                unlistedCertificate: '',
                startDateCertificate: '',
                endDateCertificate: '',
                descriptionCertificate: '',
                certificateType: 'file',
                certificateLink: '',
                certificateFile: null
            },
            certificateToDeleteRecId: null,

            ApplicantCertificate: [],

            // Courses Validation
            courseNameIsValid: false,
            startDateCourseIsValid: false,
            endDateCourseIsValid: false,
            descriptionCourseIsValid: false,

            newCourse: {
                courseName: '',
                startDateCourse: '',
                endDateCourse: '',
                descriptionCourse: ''
            },
            courseToDeleteRecId: null,

            ApplicantCourse: [],

            // Project Experience Validation
            roleIsValid: false,
            startDateProjectExperienceIsValid: false,
            endDateProjectExperienceIsValid: false,
            descriptionProjectExperienceIsValid: false,

            newProjectExperience: {
                role: '',
                startDateProjectExperience: '',
                endDateProjectExperience: '',
                descriptionProjectExperience: ''
            },
            projectExperienceToDeleteRecId: null,

            ApplicantProjectExperience: [],

            // Resume Validation
            //attachmentNameIsValid: false,
            resumeFileIsValid: false,
            resumeLinkIsValid: false,
            resumeUploadTypeIsValid: false,

            newResume: {
                fileName: '',
                resumeType: 'file',
                attachmentFile: null,
                resumeLink: ''
            },
            attachmentToDeleteRecId: null,
            attachmentContent: null,
            attachmentMimeType: '',
            attachmentFileName: '',

            ApplicantAttachment: [],

            // Reset Modal
            forgotEmail: '',
            currentPassword: '',
            newPassword: '',
            confirmNewPassword: '',
            resetSubmitted: false,
            resetCurrentPasswordIsValid: false,
            resetNewPasswordIsValid: false,
            resetConfirmNewPasswordIsValid: false,

            // For API's
            countryList: [],
            genderList: [],
            educationalLevelsList: [], // e.g. Bsc., Diploma , etc.
            educationsList: [],
            skillsList: [],
            certificatesList: [],
            ApplicantApplication: [],

            job: {
                status: 'Received', // this will be retrieved from the API 
            },

            statusColors: {
                'Interview': '#FFD1A4', // Light Peach
                'Received': '#2B2D42',  // Dark Grayish Blue 
                'Rejection': '#00013A',  // Dark Blue 
                'Confirmed': '#FC7F3F', // Orange 
                'Short Listed': '#000039', // Deep Navy	
                'Job offer': '#00B2A9', // Soft Blue-Green 
                'Offer accepted': '#4CAF50', // Green 
                'Canceled': '#D32F2F', // Red 
                'Hire new worker': '#3F51B5', // Indigo 
            }
        };
    },
    methods: {
        GetGenders: async function () {
            try {
                this.genderList = await GetLookUpGender();
            } catch (error) {
                console.error("Error fetching genders:", error);
            }
        },
        GetCountries: async function () {
            try {
                this.countryList = await GetLookUpCountryId(this.companyId);
            } catch (error) {
                console.error("Error fetching countries:", error);
            }
        },
        GetEducationLevel: async function () {
            try {
                this.educationalLevelsList = await GetLookUpEducationLevel(this.companyId);
            } catch (error) {
                console.error("Error fetching educational levels:", error);
            }
        },
        GetEducation: async function () {
            try {
                this.educationsList = await GetLookUpEducation(this.companyId);
            } catch (error) {
                console.error("Error fetching education:", error);
            }
        },
        GetSkill: async function () {
            try {
                this.skillsList = await GetLookUpSkill(this.companyId);
            } catch (error) {
                console.error("Error fetching skills:", error);
            }
        },
        GetCertificate: async function () {
            try {
                this.certificatesList = await GetLookUpCertificateType(this.companyId);
            } catch (error) {
                console.error("Error fetching certificates:", error);
            }
        },
        GetApplicantPersonalInformation: async function () {
            try {
                this.ApplicantPersonalInformation = await GetApplicantPersonalInformation(
                    this.companyId,
                    this.applicantId,
                );
                this.$nextTick(() => {
                    this.ApplicantPersonalInformation.birthDate = this.ApplicantPersonalInformation.birthDate.split('T')[0];
                });
            } catch (error) {
                console.error("Validation failed:", error);
            }
        },
        UpdateApplicant: async function () {
            try {
                const response = await UpdateApplicant(
                    this.companyId,
                    this.applicantId,
                    this.ApplicantPersonalInformation.arabicName,
                    this.ApplicantPersonalInformation.name,
                    this.ApplicantPersonalInformation.countryRegionId,
                    this.ApplicantPersonalInformation.birthDate,
                    this.ApplicantPersonalInformation.email,
                    this.ApplicantPersonalInformation.email,
                    this.ApplicantPersonalInformation.genderValue,
                    this.ApplicantPersonalInformation.phone,
                    this.ApplicantPersonalInformation.nationalId,
                    this.ApplicantPersonalInformation.linkedInLink || '',
                    this.ApplicantPersonalInformation.yearsOfExperience,
                    this.ApplicantPersonalInformation.middleName || '',
                    this.ApplicantPersonalInformation.middleName || '',
                );

                if (response && response.status === 200) {
                    toastr.success(this.$Translate('personalUpdatedSuccessfully'));
                } else {
                    toastr.success(this.$Translate('personalUpdateFailed'));
                }
                //console.log('Applicant updated:', response);
            } catch (error) {
                console.error('Error updating applicant:', error);
            }
            this.personalSubmitted = false;
            this.isDisabled = true;
        },
        update() {
            this.personalSubmitted = true;
            let valid = true;

            // Personal Info Validation:

            // Name
            if (!this.ApplicantPersonalInformation.name) {
                toastr.error(this.$Translate('nameRequired'));
                this.nameIsValid = false;
                valid = false;
            } else if (this.ApplicantPersonalInformation.name.length < 8) {
                toastr.warning(this.$Translate('nameTooShort'));
                this.nameIsValid = false;
                valid = false;
            } else if (/[^a-zA-Z\s]/.test(this.ApplicantPersonalInformation.name)) {
                toastr.warning(this.$Translate('nameInvalidChars'));
                this.nameIsValid = false;
                valid = false;
            } else {
                this.nameIsValid = true;
            }

            // Arabic Name
            this.arabicNameIsValid = true;

            // National ID
            this.nationalIdIsValid = true;

            // Country
            if (!this.ApplicantPersonalInformation.countryRegionId) {
                toastr.error(this.$Translate('countryRequired'));
                this.countryRegionIdIsValid = false;
                valid = false;
            } else {
                this.countryRegionIdIsValid = true;
            }

            // Gender
            if (!this.ApplicantPersonalInformation.genderValue) {
                toastr.error(this.$Translate('genderRequired'));
                this.genderIsValid = false;
                valid = false;
            } else {
                this.genderIsValid = true;
            }

            // Date of Birth
            if (!this.ApplicantPersonalInformation.birthDate) {
                toastr.error(this.$Translate('dateOfBirthRequired'));
                this.dateOfBirthIsValid = false;
                valid = false;
            } else {
                this.dateOfBirthIsValid = true;
            }

            // Email
            if (!this.ApplicantPersonalInformation.email) {
                toastr.error(this.$Translate('emailRequired'));
                this.emailIsValid = false;
                valid = false;
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.ApplicantPersonalInformation.email)) {
                toastr.warning(this.$Translate('invalidEmail'));
                this.emailIsValid = false;
                valid = false;
            } else {
                this.emailIsValid = true;
            }

            // Phone
            if (!this.ApplicantPersonalInformation.phone) {
                toastr.error(this.$Translate('phoneRequired'));
                this.phoneIsValid = false;
                valid = false;
            } else {
                this.phoneIsValid = true;
            }

            // LinkedIn
            if (this.ApplicantPersonalInformation.linkedInLink && !/^https?:\/\/(www\.)?linkedin\.com\/.*$/.test(this.ApplicantPersonalInformation.linkedInLink)) {
                toastr.warning(this.$Translate('invalidLinkedIn'));
                this.linkedInLinkIsValid = false;
                valid = false;
            } else {
                this.linkedInLinkIsValid = true;
            }

            // Years of Experience
            if (
                this.ApplicantPersonalInformation.yearsOfExperience === null ||
                this.ApplicantPersonalInformation.yearsOfExperience === '' ||
                isNaN(this.ApplicantPersonalInformation.yearsOfExperience)
            ) {
                toastr.error(this.$Translate('experienceRequired'));
                this.yearOfExperienceIsValid = false;
                valid = false;
            } else if (this.ApplicantPersonalInformation.yearsOfExperience < 0) {
                toastr.warning(this.$Translate('experienceInvalid'));
                this.yearOfExperienceIsValid = false;
                valid = false;
            } else {
                this.yearOfExperienceIsValid = true;
            }

            if (valid) {
                this.UpdateApplicant();
            }
        },
        GetApplicantEducation: async function () {
            try {
                this.ApplicantEducation = await GetApplicantEducation(
                    this.companyId,
                    this.applicantId,
                );
            } catch (error) {
                console.error("Validation failed:", error);
            }
        },
        async addEducation() {
            let valid = true;
            this.eduSubmitted = true;

            this.gradePointIsValid = true;
            this.startDateIsValid = true;
            this.descriptionIsValid = true;

            // Education Discipline
            if (!this.newEducation.educationDisciplineDescription) {
                toastr.error(this.$Translate('educationDisciplineRequired'));
                this.educationDisciplineValid = false;
                valid = false;
            } else {
                this.educationDisciplineIsValid = true;
            }

            // Education Institute
            if (!this.newEducation.educationInstitutionDescription) {
                toastr.error(this.$Translate('educationInstituteRequired'));
                this.educationInstituteIsValid = false;
                valid = false;
            } else {
                this.educationInstituteIsValid = true;
            }

            // Education Level
            if (!this.newEducation.educationLevelDescription) {
                toastr.error(this.$Translate('educationLevelRequired'));
                this.educationLevelIsValid = false;
                valid = false;
            } else {
                this.educationLevelIsValid = true;
            }

            // Education End Date
            if (!this.newEducation.endDate) {
                toastr.error(this.$Translate('endDateRequired'));
                this.endDateIsValid = false;
                valid = false;
            } else {
                this.endDateIsValid = true;
            }

            if (valid) {
                const today = new Date().toISOString().split('T')[0];

                try {
                    const response = await CreateApplicantEducation(
                        this.companyId,
                        this.applicantId,
                        this.newEducation.educationDisciplineDescription,
                        this.newEducation.educationInstitutionDescription,
                        Number(this.newEducation.educationLevelDescription),
                        Number(this.newEducation.gradePointAverage) || null,
                        this.newEducation.startDate || today,
                        this.newEducation.endDate,
                        this.newEducation.description
                    );

                    if (response && response.status === 200) {
                        this.ApplicantEducation.push(response);
                        toastr.success(this.$Translate('educationAddedSuccessfully'));
                        this.newEducation = {
                            educationDisciplineDescription: '',
                            educationInstitutionDescription: '',
                            educationLevelDescription: '',
                            gradePointAverage: '',
                            startDate: '',
                            endDate: '',
                            description: '',
                        };
                        this.GetApplicantEducation();
                        this.eduSubmitted = false;
                    } else {
                        this.eduSubmitted = true;
                    }
                } catch (error) {
                    console.error("Error creating applicant education:", error);
                }
            }
        },
        async deleteEducation(recId) {
            try {
                const response = await DeleteApplicantEducation(this.companyId, recId);

                if (response && response.status === 200) {
                    this.ApplicantEducation = this.ApplicantEducation.filter(
                        (education) => education.recId !== recId
                    );
                    toastr.success(this.$Translate('educationDeletedSuccessfully'));

                    this.GetApplicantEducation();
                } else {
                    toastr.error(this.$Translate('errorDeleting'));
                }
            } catch (error) {
                console.error("Error deleting applicant education:", error);
                toastr.error(this.$Translate('errorDeleting'));
            }
        },
        showModal(context, recId) {
            this.contextToDelete = context;
            if (context === 'education') {
                this.educationToDeleteRecId = recId;
            } else if (context === 'skill') {
                this.skillToDeleteRecId = recId;
            } else if (context === 'professionalExperience') {
                this.professionalExperienceToDeleteRecId = recId;
            } else if (context === 'certificate') {
                this.certificateToDeleteRecId = recId;
            } else if (context === 'course') {
                this.courseToDeleteRecId = recId;
            } else if (context === 'projectExperience') {
                this.projectExperienceToDeleteRecId = recId;
            } else if (context === 'resume') {
                this.attachmentToDeleteRecId = recId;
            }
            deleteWarningModal.show();
        },
        async confirmDelete() {
            if (this.contextToDelete === 'education' && this.educationToDeleteRecId != null) {
                await this.deleteEducation(this.educationToDeleteRecId);
                this.educationToDeleteRecId = null;
            } else if (this.contextToDelete === 'skill' && this.skillToDeleteRecId != null) {
                await this.deleteSkill(this.skillToDeleteRecId);
                this.skillToDeleteRecId = null;
            } else if (this.contextToDelete === 'professionalExperience' && this.professionalExperienceToDeleteRecId != null) {
                await this.deleteProfessionalExperience(this.professionalExperienceToDeleteRecId);
                this.professionalExperienceToDeleteRecId = null;
            } else if (this.contextToDelete === 'certificate' && this.certificateToDeleteRecId != null) {
                await this.deleteCertificate(this.certificateToDeleteRecId);
                this.certificateToDeleteRecId = null;
            } else if (this.contextToDelete === 'course' && this.courseToDeleteRecId != null) {
                await this.deleteCourse(this.courseToDeleteRecId);
                this.courseToDeleteRecId = null;
            } else if (this.contextToDelete === 'projectExperience' && this.projectExperienceToDeleteRecId != null) {
                await this.deleteProjectExperience(this.projectExperienceToDeleteRecId);
                this.projectExperienceToDeleteRecId = null;
            } else if (this.contextToDelete === 'resume' && this.attachmentToDeleteRecId != null) {
                await this.deleteResume(this.attachmentToDeleteRecId);
                this.attachmentToDeleteRecId = null;
            }
            deleteWarningModal.hide();
        },
        GetApplicantSkill: async function () {
            try {
                this.ApplicantSkill = await GetApplicantSkill(
                    this.companyId,
                    this.applicantId,
                );
            } catch (error) {
                console.error("Validation failed:", error);
            }
        },
        GetSkillRating: async function () {
            try {
                this.SkillRatingLevelList = await GetLookUpSkillRatingLevel(this.companyId, this.newSkill.recId);
            } catch (error) {
                console.error("Error fetching skill rating levels:", error);
            }
        },
        async addSkill() {
            let valid = true;
            this.skillSubmitted = true;

            this.gradePointIsValid = true;
            this.startDateIsValid = true;
            this.descriptionIsValid = true;

            // <!-- Skill Field -->
            if (!this.newSkill.recId) {
                toastr.error(this.$Translate('skillRequired'));
                this.skillIdIsValid = false;
                valid = false;
            } else {
                this.skillIdIsValid = true;
            }

            // Rating Level
            if (!this.newSkill.ratingLevelId && this.newSkill.recId) {
                toastr.error(this.$Translate('ratingLevelIdRequired'));
                this.ratingLevelIdIsValid = false;
                valid = false;
            } else {
                this.ratingLevelIdIsValid = true;
            }

            if (valid) {
                try {
                    const response = await CreateApplicantSkill(
                        this.companyId,
                        this.applicantId,
                        this.newSkill.recId,
                        this.newSkill.ratingLevelId
                    );

                    if (response && response.status === 200) {
                        this.ApplicantSkill.push(response);
                        toastr.success(this.$Translate('skillAddedSuccessfully'));
                        this.newSkill = {
                            recId: '',
                            ratingLevelId: ''
                        };
                        this.GetApplicantSkill();
                        this.skillSubmitted = false;
                    } else {
                        this.skillSubmitted = true;
                    }
                } catch (error) {
                    console.error("Error creating applicant skill:", error);
                }
            }
        },
        async deleteSkill(recId) {
            try {
                const response = await DeleteApplicantSkill(this.companyId, recId);

                if (response && response.status === 200) {
                    this.ApplicantSkill = this.ApplicantSkill.filter(
                        (skill) => skill.recId !== recId
                    );
                    toastr.success(this.$Translate('skillDeletedSuccessfully'));

                    this.GetApplicantSkill();
                } else {
                    toastr.error(this.$Translate('errorDeleting'));
                }
            } catch (error) {
                console.error("Error deleting applicant skill:", error);
                toastr.error(this.$Translate('errorDeleting'));
            }
        },
        GetApplicantCourse: async function () {
            try {
                this.ApplicantCourse = await GetApplicantCourse(
                    this.companyId,
                    this.applicantId,
                );
            } catch (error) {
                console.error("Validation failed:", error);
            }
        },
        async addCourse() {
            let valid = true;
            this.courseSubmitted = true;

            this.descriptionCourseIsValid = true;

            // Course Name
            if (!this.newCourse.courseName) {
                toastr.error(this.$Translate('courseNameRequired'));
                this.courseNameIsValid = false;
                valid = false;
            } else {
                this.courseNameIsValid = true;
            }

            // Course Start Date
            if (!this.newCourse.startDateCourse) {
                toastr.error(this.$Translate('startDateCourseRequired'));
                this.startDateCourseIsValid = false;
                valid = false;
            } else {
                this.startDateCourseIsValid = true;
            }

            // Course End Date
            if (!this.newCourse.endDateCourse) {
                toastr.error(this.$Translate('endDateCourseRequired'));
                this.endDateCourseIsValid = false;
                valid = false;
            } else {
                this.endDateCourseIsValid = true;
            }

            if (valid) {
                try {
                    const response = await CreateApplicantCourse(
                        this.companyId,
                        this.applicantId,
                        this.newCourse.courseName,
                        this.newCourse.startDateCourse,
                        this.newCourse.endDateCourse,
                        this.newCourse.descriptionCourse
                    );

                    if (response && response.status === 200) {
                        this.ApplicantCourse.push(response);
                        toastr.success(this.$Translate('courseAddedSuccessfully'));
                        this.newCourse = {
                            courseName: '',
                            startDateCourse: '',
                            endDateCourse: '',
                            descriptionCourse: ''
                        };
                        this.GetApplicantCourse();
                        this.courseSubmitted = false;
                    } else {
                        this.courseSubmitted = true;
                    }
                } catch (error) {
                    console.error("Error creating applicant course:", error);
                }
            }
        },
        async deleteCourse(recId) {
            try {
                const response = await DeleteApplicantCourse(this.companyId, recId);

                if (response && response.status === 200) {
                    this.ApplicantCourse = this.ApplicantCourse.filter(
                        (course) => course.recId !== recId
                    );
                    toastr.success(this.$Translate('courseDeletedSuccessfully'));

                    this.GetApplicantCourse();
                } else {
                    toastr.error(this.$Translate('errorDeleting'));
                }
            } catch (error) {
                console.error("Error deleting applicant education:", error);
                toastr.error(this.$Translate('errorDeleting'));
            }
        },
        GetApplicantCertificate: async function () {
            try {
                this.ApplicantCertificate = await GetApplicantCertificate(
                    this.companyId,
                    this.applicantId,
                );
            } catch (error) {
                console.error("Validation failed:", error);
            }
        },
        handleCertificateFileUpload(event) {
            const file = event.target.files[0];
            if (file) {
                this.newCertificate.certificateFile = file;
            } else {
                this.newCertificate.certificateFile = null;
            }
        },
        async addCertificate() {
            let valid = true;
            this.certSubmitted = true;

            this.unlistedCertificateIsValid = true;
            this.descriptionCertificateIsValid = true;

            // Certificate
            if (!this.newCertificate.certificateTypeId) {
                toastr.error(this.$Translate('certificateRequired'));
                this.certificateTypeIdIsValid = false;
                valid = false;
            } else {
                this.certificateTypeIdIsValid = true;
            }

            // Certificate Start Date
            if (!this.newCertificate.startDateCertificate) {
                toastr.error(this.$Translate('startDateCertificateRequired'));
                this.startDateCertificateIsValid = false;
                valid = false;
            } else {
                this.startDateCertificateIsValid = true;
            }

            // Certificate End Date
            if (!this.newCertificate.endDateCertificate) {
                toastr.error(this.$Translate('endDateCertificateRequired'));
                this.endDateCertificateIsValid = false;
                valid = false;
            } else {
                this.endDateCertificateIsValid = true;
            }

            // Radio 
            if (this.newCertificate.certificateType === 'file') {
                if (this.newCertificate.certificateFile === null) {
                    toastr.error(this.$Translate('certificateFileRequired'));
                    this.certificateUploadTypeIsValid = false;
                    valid = false;
                } else {
                    this.certificateUploadTypeIsValid = true;
                }
            } else if (this.newCertificate.certificateType === 'url') {
                if (!this.newCertificate.certificateLink) {
                    toastr.error(this.$Translate('certificateUrlRequired'));
                    this.certificateUploadTypeIsValid = false;
                    valid = false;
                } else {
                    this.certificateUploadTypeIsValid = true;
                }
            }

            if (valid) {
                try {
                    const response = await CreateApplicantCertificate(
                        this.companyId,
                        this.applicantId,
                        this.newCertificate.certificateTypeId,
                        this.newCertificate.unlistedCertificate ? this.newCertificate.unlistedCertificate : 'none',
                        this.newCertificate.startDateCertificate,
                        this.newCertificate.endDateCertificate,
                        this.newCertificate.descriptionCertificate,
                        this.newCertificate.certificateType,
                        this.newCertificate.certificateLink,
                        this.newCertificate.certificateFile
                    );

                    if (response && response.status === 200) {
                        this.ApplicantCertificate.push(response);
                        toastr.success(this.$Translate('certificateAddedSuccessfully'));
                        this.newCertificate = {
                            certificateTypeId: '',
                            unlistedCertificate: '',
                            startDateCertificate: '',
                            endDateCertificate: '',
                            descriptionCertificate: '',
                            certificateType: 'file',
                            certificateLink: '',
                        };
                        this.GetApplicantCertificate();
                        this.certSubmitted = false;
                    } else {
                        this.certSubmitted = true;
                    }
                } catch (error) {
                    console.error("Error creating applicant certificate:", error);
                }
            }
        },
        async deleteCertificate(recId) {
            try {
                const response = await DeleteApplicantCertificate(this.companyId, recId);

                if (response && response.status === 200) {
                    this.ApplicantCertificate = this.ApplicantCertificate.filter(
                        (certificate) => certificate.recId !== recId
                    );
                    toastr.success(this.$Translate('certificateDeletedSuccessfully'));

                    this.GetApplicantCertificate();
                } else {
                    toastr.error(this.$Translate('errorDeleting'));
                }
            } catch (error) {
                console.error("Error deleting applicant certificate:", error);
                toastr.error(this.$Translate('errorDeleting'));
            }
        },
        GetApplicantProfessionalExperience: async function () {
            try {
                this.ApplicantProfessionalExperience = await GetApplicantProfessionalExperience(
                    this.companyId,
                    this.applicantId,
                );
            } catch (error) {
                console.error("Validation failed:", error);
            }
        },
        async addProfessionalExperience() {
            let valid = true;
            this.profExpSubmitted = true;

            this.descriptionProfessionalExperienceIsValid = true;

            // Employer Type
            if (!this.newProfessionalExperience.employerLocation) {
                toastr.error(this.$Translate('employmentRequired'));
                this.employmentTypeIsValid = false;
                valid = false;
            } else {
                this.employmentTypeIsValid = true;
            }

            // Employer Position
            if (!this.newProfessionalExperience.employerPosition) {
                const employment = this.newProfessionalExperience.employerLocation;

                if (employment === 'Employee') {
                    toastr.error(this.$Translate('employerPositionRequired'));
                    this.employerPositionIsValid = false;
                    valid = false;
                } else if (employment === 'Unemployed') {
                    toastr.error(this.$Translate('unEmploymentTypeRequired'));
                    this.employerPositionIsValid = false;
                    valid = false;
                }
            } else {
                this.employerPositionIsValid = true;
            }

            // Employer Name
            if (this.newProfessionalExperience.employerLocation !== 'Unemployed') {
                if (!this.newProfessionalExperience.employerName) {
                    toastr.error(this.$Translate('nameOfEmployerRequired'));
                    this.nameOfEmployerIsValid = false;
                    valid = false;
                } else {
                    this.nameOfEmployerIsValid = true;
                }
            }

            // Start Date
            if (!this.newProfessionalExperience.startDate) {
                toastr.error(this.$Translate('startDateRequired'));
                this.startDateProfessionalExperienceIsValid = false;
                valid = false;
            } else {
                this.startDateProfessionalExperienceIsValid = true;
            }

            // Final Date
            if (!this.isChecked) {
                if (!this.newProfessionalExperience.endDate) {
                    toastr.error(this.$Translate('endDateRequired'));
                    this.endDateProfessionalExperienceIsValid = false;
                    valid = false;
                } else {
                    this.endDateProfessionalExperienceIsValid = true;
                }
            } else {
                this.newProfessionalExperience.endDate = null;
                this.endDateProfessionalExperienceIsValid = true;
            }

            if (this.newProfessionalExperience.employerLocation === 'Unemployed') {
                this.newProfessionalExperience.employerName = this.newProfessionalExperience.employerLocation;
            }

            if (valid) {
                const today = new Date().toISOString().split('T')[0];

                try {
                    const response = await CreateApplicantProfessionalExperience(
                        this.companyId,
                        this.applicantId,
                        this.newProfessionalExperience.employerLocation,
                        this.newProfessionalExperience.employerPosition,
                        this.newProfessionalExperience.employerName,
                        this.newProfessionalExperience.startDate,
                        this.newProfessionalExperience.endDate || null,
                        this.newProfessionalExperience.note
                    );

                    if (response && response.status === 200) {
                        this.ApplicantProfessionalExperience.push(response);
                        toastr.success(this.$Translate('professionalExperienceAddedSuccessfully'));
                        this.newProfessionalExperience = {
                            employerLocation: '',
                            employerPosition: '',
                            employerName: '',
                            startDate: '',
                            endDate: '',
                            note: ''
                        };
                        this.GetApplicantProfessionalExperience();
                        this.profExpSubmitted = false;
                    } else {
                        this.profExpSubmitted = true;
                    }
                } catch (error) {
                    console.error("Error creating applicant proffesional experience:", error);
                }
            }
        },
        async deleteProfessionalExperience(recId) {
            try {
                const response = await DeleteApplicantProfessionalExperience(this.companyId, recId);

                if (response && response.status === 200) {
                    this.ApplicantProfessionalExperience = this.ApplicantProfessionalExperience.filter(
                        (professionalExperience) => professionalExperience.recId !== recId
                    );
                    toastr.success(this.$Translate('professionalExperienceDeletedSuccessfully'));

                    this.GetApplicantProfessionalExperience();
                } else {
                    toastr.error(this.$Translate('errorDeleting'));
                }
            } catch (error) {
                console.error("Error deleting applicant professional experience:", error);
                toastr.error(this.$Translate('errorDeleting'));
            }
        },
        GetApplicantProjectExperience: async function () {
            try {
                this.ApplicantProjectExperience = await GetApplicantProjectExperience(
                    this.companyId,
                    this.applicantId,
                );
            } catch (error) {
                console.error("Validation failed:", error);
            }
        },
        async addProjectExperience() {
            let valid = true;
            this.projExpSubmitted = true;

            this.descriptionProjectExperienceIsValid = true;

            // Role
            if (!this.newProjectExperience.role) {
                toastr.error(this.$Translate('roleRequired'));
                this.roleIsValid = false;
                valid = false;
            } else {
                this.roleIsValid = true;
            }

            // Project Experience Start Date
            if (!this.newProjectExperience.startDateProjectExperience) {
                toastr.error(this.$Translate('startDateProjectExperienceRequired'));
                this.startDateProjectExperienceIsValid = false;
                valid = false;
            } else {
                this.startDateProjectExperienceIsValid = true;
            }

            // Project Experience End Date
            if (!this.newProjectExperience.endDateProjectExperience) {
                toastr.error(this.$Translate('endDateProjectExperienceRequired'));
                this.endDateProjectExperienceIsValid = false;
                valid = false;
            } else {
                this.endDateProjectExperienceIsValid = true;
            }

            if (valid) {
                try {
                    const response = await CreateApplicantProjectExperience(
                        this.companyId,
                        this.applicantId,
                        this.newProjectExperience.role,
                        this.newProjectExperience.startDateProjectExperience,
                        this.newProjectExperience.endDateProjectExperience,
                        this.newProjectExperience.descriptionProjectExperience
                    );

                    if (response && response.status === 200) {
                        this.ApplicantProjectExperience.push(response);
                        toastr.success(this.$Translate('projectExperienceAddedSuccessfully'));
                        this.newProjectExperience = {
                            role: '',
                            startDateProjectExperience: '',
                            endDateProjectExperience: '',
                            descriptionProjectExperience: ''
                        };
                        this.GetApplicantProjectExperience();
                        this.projExpSubmitted = false;
                    } else {
                        this.projExpSubmitted = true;
                    }
                } catch (error) {
                    console.error("Error creating applicant project experience:", error);
                }
            }
        },
        async deleteProjectExperience(recId) {
            try {
                const response = await DeleteApplicantProjectExperience(this.companyId, recId);

                if (response && response.status === 200) {
                    this.ApplicantProjectExperience = this.ApplicantProjectExperience.filter(
                        (projectExperience) => projectExperience.recId !== recId
                    );
                    toastr.success(this.$Translate('projectExperienceDeletedSuccessfully'));

                    this.GetApplicantProjectExperience();
                } else {
                    toastr.error(this.$Translate('errorDeleting'));
                }
            } catch (error) {
                console.error("Error deleting applicant project experience:", error);
                toastr.error(this.$Translate('errorDeleting'));
            }
        },
        GetApplicantApplication: async function () {
            try {
                this.ApplicantApplication = await GetApplicantApplication(
                    this.companyId,
                    this.applicantId,
                );
            } catch (error) {
                console.error("Validation failed:", error);
            }
        },
        handleResumeFileUpload(event) {
            const file = event.target.files[0];
            if (file) {
                this.newResume.attachmentFile = file;
            } else {
                this.newResume.attachmentFile = null;
            }
        },
        async addResume() {
            let valid = true;
            this.resumeSubmitted = true;

            /* Attachment Name
            if (!this.newResume.fileName) {
                toastr.error(this.$Translate('attachmentNameRequired'));
                this.attachmentNameIsValid = false;
                valid = false;
            } else {
                this.attachmentNameIsValid = true;
            }
            */

            // Radio 
            if (this.newResume.resumeType === 'file') {
                if (this.newResume.attachmentFile === null) {
                    toastr.error(this.$Translate('resumeFileRequired'));
                    this.resumeUploadTypeIsValid = false;
                    valid = false;
                } else {
                    this.resumeUploadTypeIsValid = true;
                }
            } else if (this.newResume.resumeType === 'url') {
                if (!this.newResume.resumeLink) {
                    toastr.error(this.$Translate('resumeUrlRequired'));
                    this.resumeUploadTypeIsValid = false;
                    valid = false;
                } else {
                    this.resumeUploadTypeIsValid = true;
                }
            }

            if (valid) {
                try {
                    const response = await AddAttachment(
                        this.companyId,
                        this.applicantId,
                        this.newResume.fileName,
                        this.newResume.resumeType,
                        this.newResume.resumeLink,
                        this.newResume.attachmentFile
                    );

                    if (response && response.status === 200) {
                        this.ApplicantAttachment.push(response);
                        toastr.success(this.$Translate('attachmentAddedSuccessfully'));
                        this.newResume = {
                            fileName: '',
                            resumeType: 'file',
                            attachmentFile: null,
                            resumeLink: ''
                        };
                        this.GetApplicantAttachment();
                        this.resumeSubmitted = false;
                    } else {
                        this.resumeSubmitted = true;
                    }
                } catch (error) {
                    console.error("Error uploading applicant attachment:", error);
                }
            }
        },
        async deleteResume(recId) {
            try {
                const response = await DeleteApplicantAttachment(this.companyId, recId);

                if (response && response.status === 200) {
                    this.ApplicantAttachment = this.ApplicantAttachment.filter(
                        (attachment) => attachment.recId !== recId
                    );
                    toastr.success(this.$Translate('attachmentDeletedSuccessfully'));

                    this.GetApplicantAttachment();
                } else {
                    toastr.error(this.$Translate('errorDeleting'));
                }
            } catch (error) {
                console.error("Error deleting applicant attachment:", error);
                toastr.error(this.$Translate('errorDeleting'));
            }
        },
        async previewAttachment(recId) {
            try {
                const response = await GetAttachment(this.companyId, recId);

                if (response && response.status === 200 && response.data) { // PDF
                    this.attachmentContent = response.data; // base64 string
                    this.attachmentMimeType = 'application/pdf';

                    const attachment = this.ApplicantAttachment.find(item => item.recId === recId);
                    if (attachment && attachment.name) {
                        this.attachmentFileName = attachment.name;
                    }

                    attachmentModal.show();
                } else if (response && response.status === 200 && !response.data) { // URL
                    const attachment = this.ApplicantAttachment.find(item => item.recId === recId);
                    if (attachment && attachment.name) {
                        window.open(attachment.name, '_blank');
                    } else {
                        toastr.error(this.$Translate('urlNotFound'));
                    }
                } else {
                    toastr.error(this.$Translate('somethingWrong'));
                }
            } catch (error) {
                console.error("Error previewing applicant attachment:", error);
                toastr.error(this.$Translate('somethingWrong'));
            }
        },
        GetApplicantAttachment: async function () {
            try {
                this.ApplicantAttachment = await GetApplicantAttachment(
                    this.companyId,
                    this.applicantId,
                );
            } catch (error) {
                console.error("Validation failed:", error);
            }
        },
        openModal() {
            resetModal.show();
        },
        ChangePassword: async function () {
            resetModal.hide();
            try {
                const result = await ChangePassword(
                    this.companyId,
                    this.applicantId,
                    this.currentPassword,
                    this.newPassword,
                    this.confirmNewPassword
                );

                //console.log("API Response:", result);

                if (result?.status === 200 && result.data?.success !== false) {
                    toastr.success(this.$Translate('passwordResetNotification'));
                    changeSuccessModal.show();
                } else {
                    // If success flag is false or not present
                    changeFailedModal.show();
                }

            } catch (error) {
                console.error("Validation failed:", error);
                changeFailedModal.show();
            }

            this.currentPassword = '';
            this.newPassword = '';
            this.confirmNewPassword = '';
            this.resetSubmitted = false;
        },
        reset() {
            this.resetSubmitted = true;
            let valid = true;

            // Reset Current Password
            if (!this.currentPassword) {
                toastr.error(this.$Translate('currentPasswordRequired'));
                this.resetCurrentPasswordIsValid = false;
                valid = false;
            } else {
                this.resetCurrentPasswordIsValid = true;
            }

            // Reset New Password
            if (!this.newPassword) {
                toastr.error(this.$Translate('newPasswordRequired'));
                this.resetNewPasswordIsValid = false;
                valid = false;
            } else if (this.newPassword.length < 8) {
                toastr.warning(this.$Translate('passwordTooShort'));
                this.resetNewPasswordIsValid = false;
                valid = false;
            } else if (!/[A-Z]/.test(this.newPassword)) {
                toastr.warning(this.$Translate('passwordUppercaseRequired'));
                this.resetNewPasswordIsValid = false;
                valid = false;
            } else if (!/[0-9]/.test(this.newPassword)) {
                toastr.warning(this.$Translate('passwordNumberRequired'));
                this.resetNewPasswordIsValid = false;
                valid = false;
            } else {
                this.resetNewPasswordIsValid = true;
            }

            // Reset Confirm New Password
            if (!this.confirmNewPassword || this.confirmNewPassword.trim() === '') {
                toastr.error(this.$Translate('confirmPasswordRequired'));
                this.resetConfirmNewPasswordIsValid = false;
                valid = false;
            } else if (this.confirmNewPassword !== this.newPassword) {
                toastr.warning(this.$Translate('confirmPasswordMismatch'));
                this.resetConfirmNewPasswordIsValid = false;
                valid = false;
            } else {
                this.resetConfirmNewPasswordIsValid = true;
            }

            if (valid) {
                this.ChangePassword();
                document.getElementById("ResetPasswordForm").reset();
            }
        },
        edit() {
            if (this.isDisabled === true) {
                this.isDisabled = false;
            } else {
                this.isDisabled = true;
            }
        },
        async checkToken() {
            if (!GetToken()) {
                this.$router.push('/Login');
            } else {
                await Promise.all([this.GetGenders(), this.GetCountries(), this.GetEducationLevel(), this.GetEducation(), this.GetSkill(), this.GetCertificate(), this.GetApplicantPersonalInformation(), this.GetApplicantEducation(), this.GetApplicantSkill(), this.GetApplicantCourse(), this.GetApplicantCertificate(), this.GetApplicantProfessionalExperience(), this.GetApplicantProjectExperience(), this.GetApplicantApplication(), this.GetApplicantAttachment()]);

                /*
                await this.GetGenders();
                await this.GetCountries();
                await this.GetEducationLevel();
                await this.GetEducation();
                await this.GetSkill();
                await this.GetCertificate();
                await this.GetApplicantPersonalInformation();
                await this.GetApplicantEducation();
                await this.GetApplicantSkill();
                await this.GetApplicantCourse();
                await this.GetApplicantCertificate();
                await this.GetApplicantProfessionalExperience();
                await this.GetApplicantProjectExperience();
                await this.GetApplicantApplication();
                await this.GetApplicantAttachment();
                */
            }
        }
    },
    async mounted() {
        BlockPage(); // manually block once

        await runWithoutBlocking(async () => {
            await this.checkToken();
        });

        UnBlockPage();


        deleteWarningModal = new Modal(document.getElementById("deleteWarningModal"), {});
        attachmentModal = new Modal(document.getElementById("AttachmentModal"), {});
        resetModal = new Modal(document.getElementById("resetModal"), {});
        changeSuccessModal = new Modal(document.getElementById("changeSuccessModal"), {});
        changeFailedModal = new Modal(document.getElementById("changeFailedModal"), {});

        const tabFromQuery = this.$route.query.tab;
        if (tabFromQuery) {
            this.activeTab = tabFromQuery;
        }
    }
};
</script>

<style scoped>
.hidden {
    display: none;
}

@media (max-width: 768px) {
    .radio-group {
        flex-direction: column;
        margin-bottom: 30px;
    }

    .radio-option {
        border-right: none;
        border-bottom: 2px solid #2B2D42;
    }

    .radio-option:last-child {
        border-bottom: none;
    }
}

.input-group {
    display: flex;
    justify-content: center;
    width: 70%;
    margin: 20px auto 0;
}

.form-control:focus {
    border-color: #00013A;
    /* Highlight color on focus */
}

.input-group-text {
    background-color: #00013A;
    color: white;
    font-weight: bold;
}

.sidebar-container {
    background-color: #e8e9eb;
    border-radius: 15px;
    min-height: 100vh auto;
}

.sidebar-item {
    padding: 8px 12px;
    border-radius: 10px;
    cursor: pointer;
    transition: background-color 0.2s ease-in-out;
    display: flex;
    gap: 12px;
}

.sidebar-item:hover {
    background-color: #d1d3d6;
}

.sidebar-item.active {
    background-color: #c2c3c5;
    font-weight: bold;
}

.icon-wrapper {
    width: 20px;
    /* Ensures icons are evenly aligned */
    display: flex;
    justify-content: center;
}

.sidebar-icon {
    color: #00013A;
    font-size: 1.1rem;
}

h4 {
    margin-bottom: -10px;
}

.col-md-4 select {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background: url('../../../../dist/assets/select.svg') no-repeat right 0.75rem center;
    background-color: #fff;
    background-size: 16px 16px;
    padding-right: 2rem;
    line-height: 1.5;
    height: calc(1.5em + 0.75rem + 2px);
}

.col-md-4 label {
    margin-bottom: 5px;
}

.add-btn {
    padding: 10px 20px;
    border-radius: 10px;
    width: 250px;
    font-weight: 500;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 6px;
}

.add-btn {
    background-color: white;
    border: 1px solid #00013A;
    color: #00013A;
}

.add-btn:hover {
    background-color: #00013A;
    border: 1px solid white;
    color: white;
}

.fileArea,
.urlArea {
    margin-top: 2px;
    width: 100%;
    padding: 10px;
    border: 1px solid #ccc;
    border-radius: 10px;
    text-align: start;
}

.card {
    border-radius: 12px;
    transition: transform 0.3s ease, box-shadow 0.3s ease, background-color 0.3s ease;
}

.card:hover {
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 6px 15px rgba(0, 0, 0, 0.15);
}

.card .badge {
    transition: transform 0.2s ease;
}

.card:hover .badge {
    transform: scale(1.05);
}

.card-body {
    padding: 1.2rem;
}

fieldset[disabled] select:disabled {
    background-color: #e9ecef;
    pointer-events: none;
}
</style>
